import { CommonModule } from '@angular/common';
import { AfterViewChecked, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { toTrustedResourceUrl } from '../../../../../helpers/trusted-resource-url.helper';

@Component({
  selector: 'l-content-html',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './content-html.component.html',
})
export class ContentHtmlComponent implements AfterViewChecked {

  @ViewChild('contentFrame')
  public contentFrame?: ElementRef<HTMLIFrameElement>;

  @Input()
  public urlPath: string = '';

  @Input()
  public entrypoint: string = '';

  @Input()
  public sourceUrl: string = '';

  @Input()
  public htmlContent: string = '';

  public get inlineHtml(): string {
    return this.htmlContent.trim();
  }

  public get safeContentUrl(): string | null {
    if (this.inlineHtml) return null;

    const resolvedUrl = this.resolveUrl();

    if (!resolvedUrl) return null;

    return toTrustedResourceUrl(resolvedUrl, {
      allowExternalHttps: true,
    });
  }

  public ngAfterViewChecked(): void {
    this.syncFrameSource();
  }

  private resolveUrl(): string {
    const sourceUrl = this.sourceUrl.trim();

    if (sourceUrl) return sourceUrl;

    const urlPath = this.urlPath.trim();
    const entrypoint = this.entrypoint.trim();

    if (this.isAbsoluteHttpUrl(urlPath)) {
      return urlPath;
    }

    if (this.isAbsoluteHttpUrl(entrypoint)) {
      return entrypoint;
    }

    if (!urlPath || !entrypoint) return '';

    const sanitizedUrlPath = urlPath.endsWith('/')
      ? urlPath.slice(0, -1)
      : urlPath;

    const sanitizedEntrypoint = entrypoint.startsWith('/')
      ? entrypoint.slice(1)
      : entrypoint;

    return `${sanitizedUrlPath}/${sanitizedEntrypoint}`;
  }

  private isAbsoluteHttpUrl(value: string): boolean {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private syncFrameSource(): void {
    const frame = this.contentFrame?.nativeElement;

    if (!frame) return;

    const nextHtml = this.inlineHtml;

    if (nextHtml) {
      if (frame.getAttribute('src') !== null) {
        frame.removeAttribute('src');
      }

      if (frame.getAttribute('srcdoc') !== nextHtml) {
        frame.setAttribute('srcdoc', nextHtml);
      }

      return;
    }

    if (frame.getAttribute('srcdoc') !== null) {
      frame.removeAttribute('srcdoc');
    }

    const nextUrl = this.safeContentUrl;
    const currentAttribute = frame.getAttribute('src');

    if (!nextUrl) {
      if (currentAttribute !== null) {
        frame.removeAttribute('src');
      }

      return;
    }

    if (currentAttribute !== nextUrl) {
      frame.setAttribute('src', nextUrl);
    }
  }
}
